import GalleryIcon from "src/assets/all-icons/gallery-icon.jsx";
import UserIcon from "src/assets/all-icons/user-icon.jsx";

import ArtistIcon from '@/assets/all-icons/artist-icon';
import CollectorFrameIcon from '@/assets/all-icons/collector-frame-icon';
import config from '@/config';
import { getCollectorFrameTheme } from '@/constants/collectorFrameThemes';
import ImageUpdateLoader from '@/shared-components/loader/special-loaders/ImageUpdateLoader';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import './styles/UserProfileImage.css';

export default function UserProfileImage({
  image,
  typeOfProfile,
  loading = false,
  className = "",
  onClick = null,
  style = {},
  userFlags = null,
  showCollectorBadge = false,
  collectorFrameThemeId = null,
  collectorFrameColor = null,
  collectorFrameColorDarker = null,
}) {
  const resolvedImage = resolveUserProfileImage(image);
  const profileType = resolveUserProfileTypeWithFallback("user", typeOfProfile);
  const isCollectorProfile = profileType === "collector";
  const hasCompletedSuarteRoad =
    Boolean(userFlags?.suarteroad_completed) ||
    Boolean(userFlags?.suarte_road_completed);
  const collectorFrameVisibilityFlag = userFlags?.collector_frame_visible;
  const shouldShowCollectorFrame =
    Boolean(showCollectorBadge) &&
    isCollectorProfile &&
    hasCompletedSuarteRoad &&
    collectorFrameVisibilityFlag !== false;
  const frameEnabledClass = shouldShowCollectorFrame ? 'user-profile-image--with-frame' : '';
  const combinedClassName = [className, frameEnabledClass].filter(Boolean).join(' ');
  const fallbackFrameThemeId =
    collectorFrameThemeId ||
    userFlags?.collector_frame_theme ||
    userFlags?.collector_frame_theme_id ||
    userFlags?.collectorFrameTheme ||
    userFlags?.collectorBadgeTheme;
  const resolvedCollectorTheme = getCollectorFrameTheme(fallbackFrameThemeId);
  const frameColor = collectorFrameColor || resolvedCollectorTheme?.base;
  const frameColorDarker = collectorFrameColorDarker || resolvedCollectorTheme?.darker;
  const collectorFrameStyle = {};
  if (frameColor) collectorFrameStyle["--collector-frame-color"] = frameColor;
  if (frameColorDarker) collectorFrameStyle["--collector-frame-color-darker"] = frameColorDarker;
  if (loading) {
    return (
      shouldShowCollectorFrame ? (
        <div className="user-profile-image-collector-frame-wrapper">
          <div
            className={`user-profile-image-loading-generic${profileType === "gallery" ? " gallery" : ""} ${combinedClassName}`}
            onClick={onClick}
            style={style}
          >
            <ImageUpdateLoader className="user-profile-image-loading-generic__spinner" />
          </div>
          <CollectorFrameIcon
            className="user-profile-image-collector-frame-icon"
            style={collectorFrameStyle}
          />
        </div>
      ) : (
        <div
          className={`user-profile-image-loading-generic${profileType === "gallery" ? " gallery" : ""} ${combinedClassName}`}
          onClick={onClick}
          style={style}
        >
          <ImageUpdateLoader className="user-profile-image-loading-generic__spinner" />
        </div>
      )
    );
  }

  if (!resolvedImage) {
    return (
      shouldShowCollectorFrame ? (
        <div className="user-profile-image-collector-frame-wrapper">
          <div
            className={`user-profile-no-image-generic${profileType === "gallery" ? " gallery" : ""} ${combinedClassName}`.trim()}
            data-profile-type={profileType}
            onClick={onClick}
            style={style}
          >
            {profileType === "gallery" ? (
              <GalleryIcon className="user-profile-no-image-generic__icon" />
            ) : profileType === "artist" ? (
              <ArtistIcon className="user-profile-no-image-generic__icon artist" />
            ) : (
              <UserIcon className="user-profile-no-image-generic__icon user" />
            )}
          </div>
          <CollectorFrameIcon
            className="user-profile-image-collector-frame-icon"
            style={collectorFrameStyle}
          />
        </div>
      ) : (
        <div
          className={`user-profile-no-image-generic${profileType === "gallery" ? " gallery" : ""} ${combinedClassName}`.trim()}
          data-profile-type={profileType}
          onClick={onClick}
          style={style}
        >
          {profileType === "gallery" ? (
            <GalleryIcon className="user-profile-no-image-generic__icon" />
          ) : profileType === "artist" ? (
            <ArtistIcon className="user-profile-no-image-generic__icon artist" />
          ) : (
            <UserIcon className="user-profile-no-image-generic__icon user" />
          )}
        </div>
      )
    );
  }

  // permitir URLs absolutas (http/https/blob)
  const src =
    resolvedImage.startsWith("http://") ||
    resolvedImage.startsWith("https://") ||
    resolvedImage.startsWith("blob:")
      ? resolvedImage
      : `${config.app.imageServiceDomain}/${resolvedImage}/w=200`;

  const baseImage = (
    <img
      src={src}
      alt="user profile pic"
      loading="lazy"
      className={`user-profile-image-generic${profileType === "gallery" ? " gallery" : ""} ${combinedClassName}`.trim()}
      data-profile-type={profileType}
      onClick={onClick}
      style={style}
    />
  );

  if (!shouldShowCollectorFrame) {
    return baseImage;
  }

  return (
    <div className="user-profile-image-collector-frame-wrapper">
      {baseImage}
      <CollectorFrameIcon
        className="user-profile-image-collector-frame-icon"
        style={collectorFrameStyle}
      />
    </div>
  );
}
